import React from 'react'
import { Stack } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useTranslation } from 'react-i18next'

import { GRAY_500 } from 'constants/styling/theme'
import { CounterInput } from 'components/common/CounterInput'
import { MUITooltip } from 'components/common/MUITooltip'
import type { ResolvedExtra, SalesforceId } from 'models/purchaseFlowRevamp'
import { MUIDivider } from 'components/common/MUIDivider'

import { PricedCheckboxRow } from './PricedCheckboxRow.component'

interface Props {
  /** Extras applicable to the current product category. */
  extras: ResolvedExtra[]
  /** Currently selected extra ids. */
  selectedExtraIds: SalesforceId[]
  /** Per-extra quantities for NUMBER-kind extras. Keyed by extra id. */
  extraQuantities?: Record<SalesforceId, number>
  /** The shell product id — for the advisory "recommended for" lookup. */
  shellProductId: SalesforceId | null
  /** Toggles an extra in/out of the selection. */
  onToggle: (extraId: SalesforceId) => void
  /** Sets the quantity for a NUMBER-kind extra. */
  onSetQuantity?: (extraId: SalesforceId, quantity: number) => void
}

/**
 * Extras section (Figma 2733:30287). Checkbox rows with description and
 * price right-aligned, separated by subtle dividers. The parent drawer
 * provides the BEIGE_200 section background, so this picker renders flat
 * (no rounded card) — F20.
 *
 * When an extra has a `recommendedForShellProductIds` list that does NOT
 * include the current shell, we show an advisory tooltip (per seed-12) —
 * NEVER a blocking error.
 */
export const ExtraServicePicker: React.FC<Props> = ({
  extras,
  selectedExtraIds,
  extraQuantities = {},
  shellProductId,
  onToggle,
  onSetQuantity,
}) => {
  const { t } = useTranslation(['purchase_flow_revamp'])

  if (extras.length === 0) return null

  return (
    <Stack gap={1.2}>

      <Stack>
        {extras.map((extra, index) => {
          const isSelected = selectedExtraIds.includes(extra.id)
          const isAdvisory =
            !!extra.recommendedForShellProductIds?.length &&
            !!shellProductId &&
            !extra.recommendedForShellProductIds.includes(shellProductId)

          return (
            <React.Fragment key={extra.id}>

              <Stack sx={{ py: 1.2 }}>
                <PricedCheckboxRow
                  checked={isSelected}
                  onChange={() => onToggle(extra.id)}
                  title={extra.displayName}
                  titleAdornment={isAdvisory &&
                    <MUITooltip content={t('purchase_flow_revamp:drawer.extra_advisory_tooltip')}>
                      <InfoOutlinedIcon sx={{ fontSize: '1.4rem', color: GRAY_500 }} />
                    </MUITooltip>
                  }
                  description={extra.description}
                  price={extra.prices[0]}
                >
                  {/* NUMBER-kind stepper: appears inline below the label when selected */}
                  {extra.kind === 'NUMBER' && isSelected &&
                    <Stack direction="row" alignItems="center" gap={1.2} sx={{ mt: 0.4 }} onClick={(e) => e.stopPropagation()}>
                      <CounterInput
                        value={extraQuantities[extra.id] ?? 1}
                        minValue={1}
                        maxValue={20}
                        onChange={(qty) => onSetQuantity?.(extra.id, qty)}
                      />
                    </Stack>
                  }
                </PricedCheckboxRow>
              </Stack>

            </React.Fragment>
          )
        })}
      </Stack>

    </Stack>
  )
}
